import { useEffect, useMemo, useState } from "react";
import { ApiError, api } from "@/api/client";
import { PageHeader } from "@/components/PageHeader";
import { usePublishProgress } from "@/components/PublishProgress";
import { Skeleton } from "@/components/Skeleton";
import { Avatar } from "@/components/Avatar";
import type { TeamMember } from "@/types";
import { roleLabel } from "@/types";

interface ManualBulkJob {
  source_url: string;
  agent_user_id: string;
  agent_name: string | null;
  job_id: string;
  job_run_id: string;
  use_ss: boolean;
  // ``"not_in_catalog"`` arrives at POST time when the pasted URL
  // didn't match any HotOffer row (so the pipeline scrapes the URL
  // directly). Otherwise null at POST time — Gemini runs inside the
  // job thread and emits the resolved value
  // ("gemini" | "fallback_source" | "fallback_empty") as a progress
  // event.
  description_source:
    | "gemini"
    | "fallback_source"
    | "fallback_empty"
    | "not_in_catalog"
    | null;
  gemini_model: string | null;
}

const PER_AGENT_STORAGE_KEY = "agencygrid.manual_bulk.per_agent_v1";
const URLS_DRAFT_STORAGE_KEY = "agencygrid.manual_bulk.urls_draft_v1";

const MYHOME_RE = /^https?:\/\/(www\.)?myhome\.ge\//i;
const SS_RE = /^https?:\/\/(www\.|home\.)?ss\.ge\//i;

function isLikelyListingUrl(url: string): boolean {
  return MYHOME_RE.test(url) || SS_RE.test(url);
}

function loadStoredPerAgent(): number {
  if (typeof window === "undefined") return 5;
  try {
    const raw = window.localStorage.getItem(PER_AGENT_STORAGE_KEY);
    const n = Number(raw);
    if (Number.isFinite(n) && n >= 1 && n <= 30) return n;
  } catch {
    /* ignore */
  }
  return 5;
}

export function ManualBulkPublish() {
  const [team, setTeam] = useState<TeamMember[] | null>(null);
  const [teamError, setTeamError] = useState<string | null>(null);

  const [urlsRaw, setUrlsRaw] = useState<string>(() => {
    if (typeof window === "undefined") return "";
    try {
      return window.localStorage.getItem(URLS_DRAFT_STORAGE_KEY) ?? "";
    } catch {
      return "";
    }
  });
  const [selectedAgents, setSelectedAgents] = useState<Set<string>>(new Set());
  const [perAgent, setPerAgent] = useState<number>(loadStoredPerAgent);

  const [useSs, setUseSs] = useState(true);
  const [mhPayBalance, setMhPayBalance] = useState(true);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { registerBatch } = usePublishProgress();

  useEffect(() => {
    try {
      window.localStorage.setItem(PER_AGENT_STORAGE_KEY, String(perAgent));
    } catch {
      /* ignore */
    }
  }, [perAgent]);

  useEffect(() => {
    try {
      window.localStorage.setItem(URLS_DRAFT_STORAGE_KEY, urlsRaw);
    } catch {
      /* ignore */
    }
  }, [urlsRaw]);

  async function loadTeam() {
    try {
      const r = await api<TeamMember[]>("/api/team");
      setTeam(r.filter((m) => m.status === "active"));
    } catch (err) {
      setTeamError(
        err instanceof Error ? err.message : "Failed to load team",
      );
    }
  }

  useEffect(() => {
    loadTeam();
  }, []);

  const sortedTeam = useMemo(() => {
    if (!team) return null;
    return [...team].sort(
      (a, b) =>
        (a.placements_today ?? 0) - (b.placements_today ?? 0) ||
        (a.full_name || a.email).localeCompare(b.full_name || b.email),
    );
  }, [team]);

  // Whitespace/comma/semicolon separated URL list. Strip blanks; keep
  // valid MyHome/SS URLs separate from rejects so we can show both
  // counts and let the director clean up before submit.
  const { validUrls, invalidUrls } = useMemo(() => {
    const tokens = urlsRaw
      .split(/[\s,;]+/)
      .map((u) => u.trim())
      .filter(Boolean);
    const seen = new Set<string>();
    const valid: string[] = [];
    const invalid: string[] = [];
    for (const t of tokens) {
      if (seen.has(t)) continue;
      seen.add(t);
      if (isLikelyListingUrl(t)) valid.push(t);
      else invalid.push(t);
    }
    return { validUrls: valid, invalidUrls: invalid };
  }, [urlsRaw]);

  function selectAllAgents() {
    if (!team) return;
    setSelectedAgents(new Set(team.map((m) => m.id)));
  }
  function clearAgents() {
    setSelectedAgents(new Set());
  }
  function toggleAgent(id: string) {
    setSelectedAgents((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // Preserve the team's load-ascending order so the round-robin's
  // first agent is the lightest one — matches what the picker shows.
  const orderedAgents = useMemo(() => {
    if (!sortedTeam) return [] as string[];
    return sortedTeam
      .filter((m) => selectedAgents.has(m.id))
      .map((m) => m.id);
  }, [sortedTeam, selectedAgents]);

  const assignmentPreview = useMemo(() => {
    if (orderedAgents.length === 0) return new Map<string, number>();
    const counts = new Map<string, number>();
    validUrls.forEach((_, idx) => {
      const aid = orderedAgents[idx % orderedAgents.length];
      counts.set(aid, (counts.get(aid) ?? 0) + 1);
    });
    return counts;
  }, [validUrls, orderedAgents]);

  const agentById = useMemo(() => {
    const m = new Map<string, TeamMember>();
    (team ?? []).forEach((t) => m.set(t.id, t));
    return m;
  }, [team]);

  // Pre-flight credential gate — surface up-front, the backend would
  // reject the whole batch otherwise.
  const uncredentialled = useMemo(() => {
    if (!team) return [] as TeamMember[];
    return team.filter(
      (m) =>
        selectedAgents.has(m.id) &&
        (!m.has_myhome_creds || !m.has_myhome_phone),
    );
  }, [team, selectedAgents]);

  const canSubmit =
    validUrls.length > 0 &&
    selectedAgents.size > 0 &&
    uncredentialled.length === 0 &&
    !submitting;

  // "Auto-pick agents": when nothing's picked, auto-select every
  // credentialled active agent in load-ascending order so a one-click
  // submit lands. Mirrors MarketPublish's autoDistribute intent.
  function autoPickAgents() {
    if (!sortedTeam) return;
    const credentialled = sortedTeam.filter(
      (m) => m.has_myhome_creds && m.has_myhome_phone,
    );
    if (credentialled.length === 0) return;
    setSelectedAgents(new Set(credentialled.map((m) => m.id)));
  }

  async function submit() {
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const r = await api<{ jobs: ManualBulkJob[]; batch_id: string }>(
        "/api/market/manual-bulk-publish",
        {
          method: "POST",
          json: {
            urls: validUrls,
            agent_user_ids: orderedAgents,
            mh_pay_balance: mhPayBalance,
            use_ss: useSs,
          },
        },
      );
      // One aggregate for the whole batch instead of one register() per
      // URL — see the same note in MarketPublish's submit().
      registerBatch({ batchId: r.batch_id, total: r.jobs.length, useSs });
      // Clear the URL draft on success — they've all been dispatched.
      setUrlsRaw("");
    } catch (err) {
      setSubmitError(
        err instanceof ApiError ? err.message : "Manual bulk publish failed",
      );
    } finally {
      setSubmitting(false);
    }
  }

  const recommendedTopMatches =
    perAgent > 0 && orderedAgents.length > 0
      ? perAgent * orderedAgents.length
      : 0;

  return (
    <div className="w-full">
      <PageHeader
        title="Manual bulk publish"
        subtitle="Paste MyHome / SS.ge listing URLs and fan them out across your team. Same round-robin as Bulk, no market filter needed."
      />

      {/* ─── STICKY ACTION BAR ─── */}
      <div className="sticky top-14 lg:top-14 z-20 -mx-4 sm:-mx-6 lg:-mx-8 mb-4 px-4 sm:px-6 lg:px-8 py-3 bg-ink-950/85 backdrop-blur-md border-b border-white/5">
        <div className="card p-3 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/[0.03] px-2 py-1">
              <span className="text-[11px] uppercase tracking-wider text-slate-500">
                Per agent
              </span>
              <button
                type="button"
                className="h-6 w-6 rounded text-slate-300 hover:bg-white/[0.06]"
                onClick={() => setPerAgent((n) => Math.max(1, n - 1))}
                aria-label="Decrease per-agent count"
              >
                −
              </button>
              <input
                type="number"
                className="w-10 bg-transparent text-center text-sm font-semibold text-slate-100 tabular-nums focus:outline-none"
                value={perAgent}
                min={1}
                max={30}
                onChange={(e) => {
                  const n = Number(e.target.value);
                  if (Number.isFinite(n) && n >= 1 && n <= 30) setPerAgent(n);
                }}
              />
              <button
                type="button"
                className="h-6 w-6 rounded text-slate-300 hover:bg-white/[0.06]"
                onClick={() => setPerAgent((n) => Math.min(30, n + 1))}
                aria-label="Increase per-agent count"
              >
                +
              </button>
            </div>

            <button
              type="button"
              className="btn-secondary text-sm"
              onClick={autoPickAgents}
              disabled={(team?.length ?? 0) === 0}
              title="Auto-pick every credentialled active agent in load-ascending order"
            >
              ⚡ Auto-pick agents
            </button>

            <div className="ml-auto flex items-center gap-2 flex-wrap">
              <label className="text-xs text-slate-400 inline-flex items-center gap-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={mhPayBalance}
                  onChange={(e) => setMhPayBalance(e.target.checked)}
                />
                MyHome balance
              </label>
              <label className="text-xs text-slate-400 inline-flex items-center gap-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={useSs}
                  onChange={(e) => setUseSs(e.target.checked)}
                />
                Also SS.ge
              </label>
              <button
                className="btn-primary"
                disabled={!canSubmit}
                onClick={submit}
                title={
                  !canSubmit
                    ? "Paste at least one listing URL and pick at least one agent"
                    : `Spawn ${validUrls.length} pipeline jobs across ${selectedAgents.size} agent${selectedAgents.size === 1 ? "" : "s"}`
                }
              >
                {submitting
                  ? "Publishing…"
                  : validUrls.length > 0 && selectedAgents.size > 0
                    ? `Publish ${validUrls.length} → ${selectedAgents.size}`
                    : "Publish"}
              </button>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {validUrls.length === 0 ? (
              <span className="text-xs text-slate-500">
                {recommendedTopMatches > 0
                  ? `Paste up to ${recommendedTopMatches} URL${recommendedTopMatches === 1 ? "" : "s"} to fill ${perAgent} × ${orderedAgents.length} agent${orderedAgents.length === 1 ? "" : "s"}.`
                  : team?.length
                    ? `Paste URLs below, then pick agents.`
                    : "Loading team…"}
              </span>
            ) : selectedAgents.size === 0 ? (
              <span className="text-xs text-amber-300/80">
                {validUrls.length} URL{validUrls.length === 1 ? "" : "s"} ready
                — pick at least one agent below.
              </span>
            ) : (
              <>
                <span className="text-xs text-slate-400">
                  Round-robin →
                </span>
                {orderedAgents.map((aid) => {
                  const agent = agentById.get(aid);
                  const count = assignmentPreview.get(aid) ?? 0;
                  if (!agent) return null;
                  return (
                    <span
                      key={aid}
                      className="inline-flex items-center gap-1.5 rounded-full border border-accent-500/40 bg-accent-500/10 px-2 py-0.5 text-[11px] text-accent-100"
                    >
                      <Avatar
                        url={agent.avatar_url}
                        name={agent.full_name}
                        email={agent.email}
                        size="xs"
                        online={agent.is_online}
                      />
                      <span className="truncate max-w-[100px]">
                        {agent.full_name?.split(" ")[0] || agent.email}
                      </span>
                      <span className="font-semibold">× {count}</span>
                    </span>
                  );
                })}
              </>
            )}
          </div>

          {invalidUrls.length > 0 && (
            <div className="text-amber-300/90 text-xs bg-amber-500/10 border border-amber-500/25 rounded p-2">
              ⚠ Skipping {invalidUrls.length} entr
              {invalidUrls.length === 1 ? "y" : "ies"} that don't look like
              MyHome.ge or home.ss.ge listing URLs:{" "}
              <span className="font-mono text-amber-200/80">
                {invalidUrls.slice(0, 3).join(", ")}
                {invalidUrls.length > 3
                  ? ` (+${invalidUrls.length - 3} more)`
                  : ""}
              </span>
            </div>
          )}

          {uncredentialled.length > 0 && (
            <div className="text-rose-300 text-xs bg-rose-500/10 border border-rose-500/25 rounded p-2">
              ⛔{" "}
              {uncredentialled.length === 1
                ? "1 picked agent"
                : `${uncredentialled.length} picked agents`}{" "}
              can't be assigned —{" "}
              {uncredentialled
                .map((m) => m.full_name?.split(" ")[0] || m.email)
                .join(", ")}{" "}
              {uncredentialled.length === 1 ? "is" : "are"} missing MyHome
              creds. Save the credentials on the agent's profile, or
              deselect them, then publish.
            </div>
          )}

          {submitError && (
            <div className="text-rose-300 text-xs bg-rose-500/10 border border-rose-500/25 rounded p-2">
              {submitError}
            </div>
          )}
        </div>
      </div>

      {/* ─── URL INPUT ─── */}
      <div className="card p-3 mb-4">
        <div className="flex items-center justify-between mb-2">
          <div className="label !mb-0">
            Listing URLs{" "}
            <span className="text-slate-600 font-normal">
              · one per line · MyHome.ge / home.ss.ge
            </span>
          </div>
          <div className="text-[11px] text-slate-500 tabular-nums">
            {validUrls.length} valid
            {invalidUrls.length > 0 && (
              <span className="text-amber-300/80">
                {" "}
                · {invalidUrls.length} skipped
              </span>
            )}
          </div>
        </div>
        <textarea
          className="input min-h-[160px] !py-2 font-mono text-xs leading-5"
          placeholder={"https://www.myhome.ge/pr/12345678/\nhttps://home.ss.ge/ka/udzravi-qoneba/-87654321"}
          value={urlsRaw}
          onChange={(e) => setUrlsRaw(e.target.value)}
        />
        <div className="mt-2 flex gap-2 flex-wrap">
          <button
            type="button"
            className="text-[11px] text-slate-400 hover:text-slate-200 px-2"
            onClick={() => setUrlsRaw("")}
            disabled={urlsRaw.length === 0}
          >
            Clear
          </button>
        </div>
      </div>

      {/* ─── AGENT PICKER ─── */}
      <div className="card p-3 mb-4">
        <div className="flex items-center justify-between mb-2">
          <div className="label !mb-0">
            Team{" "}
            <span className="text-slate-600 font-normal">
              · sorted by today's load (lightest first)
            </span>
          </div>
          {team && team.length > 0 && (
            <div className="flex gap-1">
              <button
                type="button"
                className="text-[11px] text-slate-400 hover:text-slate-200 px-2"
                onClick={selectAllAgents}
              >
                All
              </button>
              <button
                type="button"
                className="text-[11px] text-slate-400 hover:text-slate-200 px-2"
                onClick={clearAgents}
                disabled={selectedAgents.size === 0}
              >
                None
              </button>
            </div>
          )}
        </div>
        {teamError ? (
          <div className="text-rose-300 text-xs">{teamError}</div>
        ) : sortedTeam === null ? (
          <Skeleton className="h-10" />
        ) : sortedTeam.length === 0 ? (
          <div className="text-xs text-slate-500">No active team members.</div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {sortedTeam.map((m) => {
              const on = selectedAgents.has(m.id);
              const assigned = assignmentPreview.get(m.id) ?? 0;
              const today = m.placements_today ?? 0;
              const credsOk = m.has_myhome_creds && m.has_myhome_phone;
              const credIssue = !m.has_myhome_creds
                ? "No MyHome credentials saved"
                : !m.has_myhome_phone
                  ? "MyHome credential is missing the phone number"
                  : null;
              const tipParts = [
                `Published today: ${today}`,
                m.publish_persona
                  ? `Persona set:\n${m.publish_persona}`
                  : "No persona set — generic voice will be used",
              ];
              if (credIssue)
                tipParts.push(
                  `⛔ ${credIssue}. Save creds on /team/${m.id} before assigning.`,
                );
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => toggleAgent(m.id)}
                  className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs transition ${
                    !credsOk
                      ? on
                        ? "border-rose-500/50 bg-rose-500/10 text-rose-200"
                        : "border-rose-500/30 bg-rose-500/[0.04] text-rose-300/80 hover:border-rose-500/50"
                      : on
                        ? "border-accent-500/50 bg-accent-500/10 text-accent-200"
                        : "border-white/10 bg-white/[0.02] text-slate-300 hover:border-white/20 hover:text-slate-100"
                  }`}
                  title={tipParts.join("\n\n")}
                >
                  <Avatar
                    url={m.avatar_url}
                    name={m.full_name}
                    email={m.email}
                    size="xs"
                    online={m.is_online}
                  />
                  <span className="truncate max-w-[140px]">
                    {m.full_name || m.email}
                  </span>
                  <span className="text-[10px] uppercase tracking-wider text-slate-500">
                    {roleLabel(m.role)}
                  </span>
                  <span
                    className={`text-[10px] tabular-nums px-1.5 py-0.5 rounded-full ${
                      today === 0
                        ? "bg-emerald-500/15 text-emerald-300"
                        : today < 5
                          ? "bg-white/[0.06] text-slate-300"
                          : "bg-amber-500/15 text-amber-300"
                    }`}
                    title={`${today} placement${today === 1 ? "" : "s"} today`}
                  >
                    {today}
                  </span>
                  {on && assigned > 0 && (
                    <span className="text-[10px] font-semibold text-accent-300">
                      ×{assigned}
                    </span>
                  )}
                  {!credsOk && (
                    <span
                      className="text-[10px] text-rose-300"
                      title={credIssue ?? undefined}
                    >
                      ⛔
                    </span>
                  )}
                  {credsOk && !m.publish_persona && (
                    <span
                      className="text-[10px] text-amber-300/80"
                      title="No persona set"
                    >
                      ⚠
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
